import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { FaChevronDown, FaSearch, FaUser, FaUsers } from 'react-icons/fa';
import { cn } from '@/lib/utils/cn';
import { Button } from './button';
import { Card, CardContent } from './card';
import Markdown from './markdown';

export type FAQSource = {
  title: string;
  url?: string;
  type?: 'medical' | 'association' | 'research' | 'testimonial' | 'expert';
};

export type FAQItem = {
  id: string;
  question: string;
  answer: string;
  category?: string;
  sources?: FAQSource[];
};

const faqVariants = cva('', {
  variants: {
    variant: {
      accordion: 'space-y-4',
      cards: 'grid gap-6',
      minimal: 'space-y-6',
    },
    layout: {
      single: '',
      columns: 'md:grid-cols-2',
    },
  },
  defaultVariants: {
    variant: 'accordion',
    layout: 'single',
  },
});

export type FAQProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof faqVariants> & {
    faqs: FAQItem[];
    searchable?: boolean;
    showCategories?: boolean;
    defaultOpen?: string[];
  };

function FAQ({
  faqs,
  className,
  variant = 'accordion',
  layout = 'single',
  searchable = false,
  showCategories = false,
  defaultOpen = [],
  ...props
}: FAQProps) {
  const [openItems, setOpenItems] = React.useState<Set<string>>(new Set(defaultOpen));
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');

  // Filter FAQs based on search and category
  const filteredFAQs = React.useMemo(() => {
    return faqs.filter((faq) => {
      const matchesSearch =
        !searchTerm || faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [faqs, searchTerm, selectedCategory]);

  // Get unique categories
  const categories = React.useMemo(() => {
    const cats = Array.from(new Set(faqs.map((faq) => faq.category).filter((cat): cat is string => Boolean(cat))));
    return ['all', ...cats];
  }, [faqs]);

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className={cn('space-y-8', className)} itemScope itemType="https://schema.org/FAQPage" {...props}>
      {(searchable || showCategories) && (
        <div className="space-y-4">
          {searchable && (
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4">
                <FaSearch />
              </div>
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          {showCategories && categories.length > 2 && (
            <div className="flex justify-center">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category === 'all' ? 'All' : category}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={cn(faqVariants({ variant, layout }))}>
        {filteredFAQs.map((faq) => (
          <FAQItem key={faq.id} faq={faq} variant={variant || 'accordion'} isOpen={openItems.has(faq.id)} onToggle={() => toggleItem(faq.id)} />
        ))}
      </div>

      {filteredFAQs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No FAQs found matching your criteria.</p>
          {searchTerm && (
            <Button variant="outline" size="sm" onClick={() => setSearchTerm('')} className="mt-2">
              Clear search
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

type FAQItemProps = {
  faq: FAQItem;
  variant: 'accordion' | 'cards' | 'minimal';
  isOpen: boolean;
  onToggle: () => void;
};

const getSourceIcon = (type?: FAQSource['type']) => {
  switch (type) {
    case 'medical':
      return <FaUser className="w-3 h-3" />;
    case 'research':
      return <FaSearch className="w-3 h-3" />;
    case 'association':
      return <FaUsers className="w-3 h-3" />;
    case 'testimonial':
      return <FaUsers className="w-3 h-3" />;
    case 'expert':
      return <FaUser className="w-3 h-3" />;
    default:
      return <FaSearch className="w-3 h-3" />;
  }
};

const SourcesList: React.FC<{ sources: FAQSource[] }> = ({ sources }) => {
  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border/50">
      <div className="text-xs text-muted-foreground mb-2 font-medium">Sources :</div>
      <div className="space-y-1">
        {sources.map((source, index) => (
          <div key={source.url || source.title || index} className="flex items-center gap-2 text-xs">
            {getSourceIcon(source.type)}
            {source.url ? (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline flex items-center gap-1"
              >
                {source.title}
                <FaSearch className="w-2 h-2" />
              </a>
            ) : (
              <span className="text-muted-foreground">{source.title}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const FAQItem: React.FC<FAQItemProps> = ({ faq, variant, isOpen, onToggle }) => {
  if (variant === 'cards') {
    return (
      <Card className="overflow-hidden" itemScope itemType="https://schema.org/Question">
        <button type="button" onClick={onToggle} className="w-full text-left p-6 hover:bg-muted/50 transition-colors">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="font-semibold text-lg" itemProp="name">
                {faq.question}
              </h3>
              {faq.category && <span className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{faq.category}</span>}
            </div>
            <div className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0', isOpen && 'transform rotate-180')}>
              <FaChevronDown />
            </div>
          </div>
        </button>
        {isOpen && (
          <CardContent className="pt-0 pb-6">
            <div className="prose prose-sm max-w-none text-muted-foreground" itemScope itemType="https://schema.org/Answer">
              <div itemProp="text">
                <Markdown>{faq.answer}</Markdown>
              </div>
            </div>
            <SourcesList sources={faq.sources || []} />
          </CardContent>
        )}
      </Card>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className="border-b border-border pb-6" itemScope itemType="https://schema.org/Question">
        <button type="button" onClick={onToggle} className="w-full text-left group">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="font-semibold text-lg group-hover:text-primary transition-colors" itemProp="name">
                {faq.question}
              </h3>
              {faq.category && <span className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{faq.category}</span>}
            </div>
            <div className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0', isOpen && 'transform rotate-180')}>
              <FaChevronDown />
            </div>
          </div>
        </button>
        {isOpen && (
          <div className="mt-4">
            <div className="prose prose-sm max-w-none text-muted-foreground" itemScope itemType="https://schema.org/Answer">
              <div itemProp="text">
                <Markdown>{faq.answer}</Markdown>
              </div>
            </div>
            <SourcesList sources={faq.sources || []} />
          </div>
        )}
      </div>
    );
  }

  // Default accordion variant
  return (
    <div className="border border-border rounded-lg overflow-hidden" itemScope itemType="https://schema.org/Question">
      <button type="button" onClick={onToggle} className="w-full text-left p-6 hover:bg-muted/50 transition-colors">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h3 className="font-semibold text-lg" itemProp="name">
              {faq.question}
            </h3>
            {faq.category && <span className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{faq.category}</span>}
          </div>
          <div className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0', isOpen && 'transform rotate-180')}>
            <FaChevronDown />
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-border bg-muted/20">
          <div className="pt-4 prose prose-sm max-w-none text-muted-foreground" itemScope itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Markdown>{faq.answer}</Markdown>
            </div>
          </div>
          <SourcesList sources={faq.sources || []} />
        </div>
      )}
    </div>
  );
};

export type FAQCTAProps = React.HTMLAttributes<HTMLDivElement> & {
  title?: string;
  description?: string;
  ctaText?: string;
  ctaHref?: string;
  onCTAClick?: () => void;
};

function FAQCTA({
  title = 'Still have questions?',
  description = "Can't find the answer you're looking for? Our support team is here to help.",
  ctaText = 'Contact Support',
  ctaHref,
  onCTAClick,
  className,
  ...props
}: FAQCTAProps) {
  return (
    <Card className={cn('text-center', className)} {...props}>
      <CardContent className="p-8">
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <p className="text-muted-foreground mb-6">{description}</p>
        <Button size="lg" onClick={onCTAClick}>
          {ctaHref ? <a href={ctaHref}>{ctaText}</a> : ctaText}
        </Button>
      </CardContent>
    </Card>
  );
}

// Add subcomponents to main component
FAQ.CTA = FAQCTA;

export default FAQ;
